import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  TranslationService,
  TranslationJob,
} from "../services/TranslationService";
import GitHubService, {
  PullRequestMergeMethod,
} from "../services/GitHubService";
import JobProgressEstimator from "../components/JobProgressEstimator";
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  GitHub as GitHubIcon,
  Translate as TranslateIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  ArrowForward as ArrowForwardIcon,
  AccessTime as AccessTimeIcon,
  Language as LanguageIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";

interface RepoDateGroup {
  groupKey: string;
  groupLabel: string;
  requestId?: string;
  dateKey: string;
  jobs: TranslationJob[];
}

type LivePullRequestState = {
  merged: boolean;
  state: "open" | "closed";
};
type DashboardJobFilter = "active" | "in_progress" | "completed" | "all";

// Helper function to group jobs by repository and then by date
const groupJobsByRepo = (jobs: TranslationJob[]) => {
  const grouped = jobs.reduce<Record<string, TranslationJob[]>>((acc, job) => {
    const repoKey = `${job.owner}/${job.repo}`;
    if (!acc[repoKey]) {
      acc[repoKey] = [];
    }
    acc[repoKey].push(job);
    return acc;
  }, {});

  // Sort jobs within each repo by creation date (newest first)
  Object.keys(grouped).forEach((key) => {
    grouped[key].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  });

  return grouped;
};

const groupJobsByDate = (jobs: TranslationJob[]): RepoDateGroup[] => {
  const grouped = jobs.reduce<Record<string, TranslationJob[]>>((acc, job) => {
    const groupKey = job.requestId
      ? `request:${job.requestId}`
      : `date:${job.createdAt.toISOString().slice(0, 10)}`;
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(job);
    return acc;
  }, {});

  return Object.entries(grouped)
    .map(([groupKey, groupedJobs]) => {
      const sortedJobs = groupedJobs.sort(
        (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
      );
      const latestDate = sortedJobs[0]?.createdAt || new Date();
      const dateKey = latestDate.toISOString().slice(0, 10);
      const dateLabel = new Intl.DateTimeFormat("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(`${dateKey}T00:00:00`));
      const requestId = sortedJobs[0]?.requestId;
      const shortRequestId = requestId ? requestId.slice(-8).toUpperCase() : "";

      return {
        groupKey,
        requestId,
        dateKey,
        groupLabel: requestId
          ? `Translation Request ${shortRequestId} • ${dateLabel}`
          : dateLabel,
        jobs: sortedJobs,
      };
    })
    .sort((left, right) => right.dateKey.localeCompare(left.dateKey));
};

// Helper function to get status color
const getStatusColor = (status: string) => {
  switch (status) {
    case "completed":
      return "success";
    case "failed":
      return "error";
    case "in-progress":
      return "primary";
    case "queued":
    default:
      return "warning";
  }
};

// Helper function to get human-readable date
const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  }).format(date);
};

const Dashboard: React.FC = () => {
  const [jobs, setJobs] = useState<TranslationJob[]>([]);
  const [jobFilter, setJobFilter] =
    useState<DashboardJobFilter>("in_progress");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRepo, setExpandedRepo] = useState<string | false>(false);
  const [collapsedDateGroups, setCollapsedDateGroups] = useState<
    Record<string, boolean>
  >({});
  const [mergingGroupKeys, setMergingGroupKeys] = useState<
    Record<string, boolean>
  >({});
  const [mergeGroupMessages, setMergeGroupMessages] = useState<
    Record<string, { severity: "success" | "warning" | "error"; text: string }>
  >({});
  const [livePullRequestStateByJob, setLivePullRequestStateByJob] = useState<
    Record<string, LivePullRequestState>
  >({});

  const { user, githubConnected } = useAuth();
  const navigate = useNavigate();

  // Fetch jobs from TranslationService
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const subscribeJobs = () => {
      if (!user?.uid) return;

      setLoading(true);
      try {
        // Use real-time subscription instead of one-time fetch
        unsubscribe = TranslationService.subscribeToUserJobs(
          user.uid,
          (updatedJobs) => {
            setJobs(updatedJobs);
            setLoading(false);
            setError(null);
          }
        );
      } catch (err: any) {
        console.error("Error subscribing to translation jobs:", err);
        setError(`Failed to load translation jobs: ${err.message}`);
        setLoading(false);
      }
    };

    subscribeJobs();

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]);

  // Handle accordion expansion
  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedRepo(isExpanded ? panel : false);
    };

  const handleDateGroupToggle = (groupKey: string) => {
    setCollapsedDateGroups((currentState) => ({
      ...currentState,
      [groupKey]: !currentState[groupKey],
    }));
  };

  const mergeMethod: PullRequestMergeMethod = "squash";

  const isJobVisibleForFilter = (
    job: TranslationJob,
    filter: DashboardJobFilter
  ) => {
    if (filter === "all") return true;
    if (filter === "completed") return job.status === "completed";
    if (filter === "in_progress") return job.status === "in-progress";
    return job.status === "in-progress" || job.status === "queued";
  };

  useEffect(() => {
    if (!user?.uid || !user.token) {
      return;
    }

    const pendingSyncJobs = jobs.filter((job) => {
      if (job.status !== "completed" || typeof job.prNumber !== "number") {
        return false;
      }

      if (job.prMergeStatus === "merged" || job.prMergedAt) {
        return false;
      }

      return !livePullRequestStateByJob[job.jobId];
    });

    if (pendingSyncJobs.length === 0) {
      return;
    }

    let cancelled = false;
    const github = new GitHubService(user.token);

    (async () => {
      const checks = await Promise.allSettled(
        pendingSyncJobs.map(async (job) => {
          const pullRequest = await github.getPullRequest(
            job.owner,
            job.repo,
            job.prNumber as number
          );

          return {
            jobId: job.jobId,
            merged: pullRequest.merged,
            state: pullRequest.state,
          };
        })
      );

      if (cancelled) {
        return;
      }

      const liveUpdates: Record<string, LivePullRequestState> = {};
      const mergedJobIds: string[] = [];

      checks.forEach((result) => {
        if (result.status !== "fulfilled") {
          return;
        }

        liveUpdates[result.value.jobId] = {
          merged: result.value.merged,
          state: result.value.state,
        };

        if (result.value.merged) {
          mergedJobIds.push(result.value.jobId);
        }
      });

      if (Object.keys(liveUpdates).length > 0) {
        setLivePullRequestStateByJob((prev) => ({ ...prev, ...liveUpdates }));
      }

      if (mergedJobIds.length > 0) {
        await Promise.allSettled(
          mergedJobIds.map((jobId) =>
            TranslationService.markJobPullRequestMerged(
              user.uid,
              jobId,
              mergeMethod
            )
          )
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [jobs, livePullRequestStateByJob, mergeMethod, user?.token, user?.uid]);

  // Navigate to create new job
  const handleCreateJob = (owner?: string, repo?: string) => {
    if (owner && repo) {
      navigate(`/job/new?owner=${owner}&repo=${repo}`);
    } else {
      navigate("/repositories");
    }
  };

  const handleBulkMergeGroup = async (
    owner: string,
    repo: string,
    groupKey: string,
    groupJobs: TranslationJob[]
  ) => {
    if (!user?.uid || !user.token) {
      setMergeGroupMessages((prev) => ({
        ...prev,
        [groupKey]: {
          severity: "error",
          text: "Connect a GitHub token in Settings before merging pull requests.",
        },
      }));
      return;
    }

    const mergeableJobs = groupJobs.filter(
      (job) =>
        job.status === "completed" &&
        typeof job.prNumber === "number" &&
        !job.prMergedAt &&
        job.prMergeStatus !== "merged" &&
        !livePullRequestStateByJob[job.jobId]?.merged
    );

    if (mergeableJobs.length === 0) {
      setMergeGroupMessages((prev) => ({
        ...prev,
        [groupKey]: {
          severity: "warning",
          text: "No merge-ready pull requests were found for this translation request.",
        },
      }));
      return;
    }

    setMergingGroupKeys((prev) => ({ ...prev, [groupKey]: true }));
    setMergeGroupMessages((prev) => {
      const next = { ...prev };
      delete next[groupKey];
      return next;
    });

    const github = new GitHubService(user.token);
    let mergedCount = 0;
    let failedCount = 0;

    try {
      for (const job of mergeableJobs) {
        try {
          const pullNumber = job.prNumber as number;
          const pullRequest = await github.getPullRequest(owner, repo, pullNumber);

          if (pullRequest.merged) {
            await TranslationService.markJobPullRequestMerged(
              user.uid,
              job.jobId,
              mergeMethod
            );
            mergedCount += 1;
            continue;
          }

          await github.mergePullRequest(owner, repo, pullNumber, mergeMethod);
          await TranslationService.markJobPullRequestMerged(
            user.uid,
            job.jobId,
            mergeMethod
          );
          mergedCount += 1;
        } catch (mergeError: any) {
          failedCount += 1;
          try {
            await TranslationService.markJobPullRequestMergeFailed(
              user.uid,
              job.jobId,
              mergeError?.message || "Unknown merge error",
              mergeMethod
            );
          } catch {
            // Ignore status-write errors and continue processing remaining PRs.
          }
        }
      }

      setMergeGroupMessages((prev) => ({
        ...prev,
        [groupKey]: failedCount
          ? {
              severity: "warning",
              text: `Merged ${mergedCount} pull request(s). ${failedCount} failed; open each PR for the exact GitHub reason.`,
            }
          : {
              severity: "success",
              text: `Merged ${mergedCount} pull request(s) successfully.`,
            },
      }));
    } finally {
      setMergingGroupKeys((prev) => ({ ...prev, [groupKey]: false }));
    }
  };

  const filteredJobs = jobs.filter((job) => isJobVisibleForFilter(job, jobFilter));

  // Group jobs by repository
  const groupedJobs = groupJobsByRepo(filteredJobs);
  const repositories = Object.keys(groupedJobs);

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ display: "flex", alignItems: "center" }}
        >
          <TranslateIcon sx={{ mr: 1 }} />
          Translation Dashboard
        </Typography>

        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleCreateJob()}
          >
            New Translation
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <JobProgressEstimator jobs={jobs} />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : jobs.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            No translation jobs found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Create your first translation job to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleCreateJob()}
          >
            Create Translation Job
          </Button>
        </Paper>
      ) : repositories.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            No jobs match this filter
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Change the status filter to view other translation jobs.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" onClick={() => setJobFilter("active")}>
              Active
            </Button>
            <Button size="small" variant="outlined" onClick={() => setJobFilter("in_progress")}>
              In Progress
            </Button>
            <Button size="small" variant="outlined" onClick={() => setJobFilter("completed")}>
              Completed
            </Button>
            <Button size="small" variant="outlined" onClick={() => setJobFilter("all")}>
              All
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 3,
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 1.5,
            }}
          >
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Job Status Filter
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredJobs.length} of {jobs.length} job
                {jobs.length === 1 ? "" : "s"}.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant={jobFilter === "active" ? "contained" : "outlined"}
                onClick={() => setJobFilter("active")}
              >
                Active
              </Button>
              <Button
                size="small"
                variant={jobFilter === "in_progress" ? "contained" : "outlined"}
                onClick={() => setJobFilter("in_progress")}
              >
                In Progress
              </Button>
              <Button
                size="small"
                variant={jobFilter === "completed" ? "contained" : "outlined"}
                onClick={() => setJobFilter("completed")}
              >
                Completed
              </Button>
              <Button
                size="small"
                variant={jobFilter === "all" ? "contained" : "outlined"}
                onClick={() => setJobFilter("all")}
              >
                All
              </Button>
            </Box>
          </Paper>

          {/* Repository overview stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Repositories
                  </Typography>
                  <Typography variant="h4" sx={{ mt: 1 }}>
                    {repositories.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Translation Jobs
                  </Typography>
                  <Typography variant="h4" sx={{ mt: 1 }}>
                    {jobs.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Completed Jobs
                  </Typography>
                  <Typography variant="h4" sx={{ mt: 1 }}>
                    {jobs.filter((job) => job.status === "completed").length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    In Progress Jobs
                  </Typography>
                  <Typography variant="h4" sx={{ mt: 1 }}>
                    {
                      jobs.filter(
                        (job) =>
                          job.status === "in-progress" ||
                          job.status === "queued"
                      ).length
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Repositories with their translation jobs */}
          {repositories.map((repoKey) => {
            const [owner, repo] = repoKey.split("/");
            const repoJobs = groupedJobs[repoKey];
            const repoDateGroups = groupJobsByDate(repoJobs);

            // Count status types for this repo
            const statusCounts = {
              completed: repoJobs.filter((job) => job.status === "completed")
                .length,
              inProgress: repoJobs.filter((job) => job.status === "in-progress")
                .length,
              queued: repoJobs.filter((job) => job.status === "queued").length,
              failed: repoJobs.filter((job) => job.status === "error").length,
            };

            return (
              <Accordion
                key={repoKey}
                expanded={expandedRepo === repoKey}
                onChange={handleChange(repoKey)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ "&.Mui-expanded": { bgcolor: "action.selected" } }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <GitHubIcon sx={{ mr: 1.5, color: "text.secondary" }} />
                      <Typography variant="h6">{repoKey}</Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mr: 2,
                      }}
                    >
                      {statusCounts.completed > 0 && (
                        <Chip
                          size="small"
                          color="success"
                          icon={<CheckIcon />}
                          label={`${statusCounts.completed} completed`}
                        />
                      )}

                      {statusCounts.inProgress > 0 && (
                        <Chip
                          size="small"
                          color="primary"
                          icon={<AccessTimeIcon />}
                          label={`${statusCounts.inProgress} in progress`}
                        />
                      )}

                      {statusCounts.queued > 0 && (
                        <Chip
                          size="small"
                          color="warning"
                          icon={<AccessTimeIcon />}
                          label={`${statusCounts.queued} queued`}
                        />
                      )}

                      {statusCounts.failed > 0 && (
                        <Chip
                          size="small"
                          color="error"
                          icon={<ErrorIcon />}
                          label={`${statusCounts.failed} failed`}
                        />
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Box
                    sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleCreateJob(owner, repo)}
                      size="small"
                    >
                      New Translation in This Repo
                    </Button>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  <Stack spacing={3}>
                    {repoDateGroups.map((dateGroup) => {
                      const localesForDate = Array.from(
                        new Set(dateGroup.jobs.map((job) => job.locale))
                      ).sort((left, right) => left.localeCompare(right));
                      const dateGroupKey = `${repoKey}_${dateGroup.groupKey}`;
                      const isDateGroupExpanded =
                        collapsedDateGroups[dateGroupKey] !== true;
                      const mergeableCount = dateGroup.jobs.filter((job) => {
                        const liveState = livePullRequestStateByJob[job.jobId];
                        const isMerged =
                          job.prMergeStatus === "merged" ||
                          Boolean(job.prMergedAt) ||
                          liveState?.merged;

                        return (
                          job.status === "completed" &&
                          typeof job.prNumber === "number" &&
                          !isMerged
                        );
                      }).length;
                      const isMerging = mergingGroupKeys[dateGroupKey] === true;
                      const mergeGroupMessage = mergeGroupMessages[dateGroupKey];

                      return (
                        <Accordion
                          key={dateGroupKey}
                          expanded={isDateGroupExpanded}
                          onChange={() => handleDateGroupToggle(dateGroupKey)}
                          disableGutters
                          elevation={0}
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            overflow: "hidden",
                            "&:before": { display: "none" },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              px: 2,
                              py: 0.5,
                              bgcolor: "background.default",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: { xs: "flex-start", md: "center" },
                                flexDirection: { xs: "column", md: "row" },
                                gap: 1.5,
                                width: "100%",
                              }}
                            >
                              <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {dateGroup.groupLabel}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {dateGroup.jobs.length} job
                                  {dateGroup.jobs.length === 1 ? "" : "s"} across{" "}
                                  {localesForDate.length} locale
                                  {localesForDate.length === 1 ? "" : "s"}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  gap: 0.75,
                                  mr: 1,
                                }}
                              >
                                {dateGroup.requestId && (
                                  <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label="Batch"
                                  />
                                )}
                                <Chip
                                  size="small"
                                  color={mergeableCount > 0 ? "success" : "default"}
                                  variant="outlined"
                                  label={`${mergeableCount} merge-ready PR${
                                    mergeableCount === 1 ? "" : "s"
                                  }`}
                                />
                                {localesForDate.map((localeCode) => (
                                  <Chip
                                    key={`${dateGroup.groupKey}_${localeCode}`}
                                    size="small"
                                    variant="outlined"
                                    label={localeCode.toUpperCase()}
                                  />
                                ))}
                              </Box>
                            </Box>
                          </AccordionSummary>

                          <AccordionDetails sx={{ p: 2 }}>
                            <Box
                              sx={{
                                mb: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: { xs: "stretch", md: "center" },
                                flexDirection: { xs: "column", md: "row" },
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                Merge all completed locale PRs for this translation request.
                              </Typography>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                disabled={
                                  !githubConnected ||
                                  mergeableCount === 0 ||
                                  isMerging
                                }
                                onClick={() =>
                                  handleBulkMergeGroup(
                                    owner,
                                    repo,
                                    dateGroupKey,
                                    dateGroup.jobs
                                  )
                                }
                                startIcon={
                                  isMerging ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <CheckIcon />
                                  )
                                }
                              >
                                {isMerging
                                  ? "Merging..."
                                  : `Merge ${mergeableCount} PR${
                                      mergeableCount === 1 ? "" : "s"
                                    }`}
                              </Button>
                            </Box>

                            {mergeGroupMessage && (
                              <Alert severity={mergeGroupMessage.severity} sx={{ mb: 2 }}>
                                {mergeGroupMessage.text}
                              </Alert>
                            )}

                            <Grid container spacing={2}>
                              {dateGroup.jobs.map((job) => (
                                <Grid item xs={12} md={6} key={job.jobId}>
                                  <Card variant="outlined">
                                    <CardContent sx={{ pb: 1 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="subtitle1"
                                          fontWeight="bold"
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <LanguageIcon
                                            sx={{ mr: 0.5, fontSize: "1rem" }}
                                          />
                                          {job.locale.toUpperCase()}
                                        </Typography>
                                        <Chip
                                          size="small"
                                          label={job.status}
                                          color={getStatusColor(job.status) as any}
                                        />
                                      </Box>

                                      <Typography
                                        variant="body2"
                                        sx={{ mb: 2 }}
                                        noWrap
                                        title={job.sourcePath}
                                      >
                                        Source: {job.sourcePath}
                                      </Typography>

                                      <Box
                                        sx={{
                                          mb: 2,
                                        }}
                                      >
                                        <LinearProgress
                                          variant="determinate"
                                          value={job.progress || 0}
                                          color={getStatusColor(job.status) as any}
                                          sx={{ height: 8, borderRadius: 4 }}
                                        />
                                        <Box
                                          sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            mt: 0.5,
                                          }}
                                        >
                                          <Typography variant="caption">
                                            {job.completedChunks} of{" "}
                                            {job.totalChunks} chunks
                                          </Typography>
                                          <Typography variant="caption">
                                            {Math.round(job.progress)}%
                                          </Typography>
                                        </Box>
                                      </Box>

                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          mb: 1,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Created: {formatDate(job.createdAt)}
                                        </Typography>

                                        <Box>
                                          <Tooltip title="Job Details">
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              onClick={() =>
                                                navigate(`/job/${job.jobId}`)
                                              }
                                            >
                                              <ArrowForwardIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      </Box>

                                      {typeof job.prNumber === "number" && (
                                        <Box
                                          sx={{
                                            mb: 1.5,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1,
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            PR #{job.prNumber}
                                          </Typography>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 0.75,
                                            }}
                                          >
                                            {(job.prMergeStatus === "merged" ||
                                              job.prMergedAt ||
                                              livePullRequestStateByJob[job.jobId]
                                                ?.merged) && (
                                              <Chip
                                                size="small"
                                                color="success"
                                                label="Merged"
                                              />
                                            )}
                                            {job.prMergeStatus !== "merged" &&
                                              !job.prMergedAt &&
                                              !livePullRequestStateByJob[job.jobId]
                                                ?.merged &&
                                              livePullRequestStateByJob[job.jobId]
                                                ?.state === "closed" && (
                                                <Chip
                                                  size="small"
                                                  color="default"
                                                  label="Closed"
                                                />
                                              )}
                                            {job.prMergeStatus === "failed" && (
                                              <Chip
                                                size="small"
                                                color="error"
                                                label="Merge failed"
                                              />
                                            )}
                                            {job.prUrl && (
                                              <Button
                                                size="small"
                                                variant="text"
                                                href={job.prUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                              >
                                                Open PR
                                              </Button>
                                            )}
                                          </Box>
                                        </Box>
                                      )}

                                      {job.status === "completed" && (
                                        <Box
                                          sx={{
                                            mt: 2,
                                            pt: 2,
                                            borderTop: "1px solid #eee",
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            color="primary"
                                          >
                                            <NotificationsIcon
                                              fontSize="small"
                                              sx={{
                                                mr: 1,
                                                verticalAlign: "middle",
                                              }}
                                            />
                                            Email Notification
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            A completion notification has been
                                            sent to your email address.
                                          </Typography>
                                        </Box>
                                      )}
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      )}
    </Box>
  );
};

export default Dashboard;
